import { DataSource } from "@angular/cdk/collections";
import { UserDetails } from "../models/UserDetails";
import { BehaviorSubject, merge, Observable, Subject } from "rxjs";
import { UserService } from "../http/table-data-services/User.service";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { map, takeUntil } from "rxjs/operators";

export class UserDataSource extends DataSource<UserDetails> {
  filterChange = new BehaviorSubject<string>('');
  filteredData: UserDetails[] = [];
  renderedData: UserDetails[] = [];

  // ✅ Subject para desuscribirse cuando el datasource se destruye
  private destroy$ = new Subject<void>();

  constructor(
    public businessGroupId: string | null,
    public exampleDatabase: UserService,
    public paginator: MatPaginator,
    public _sort: MatSort,
    public idSucursal: string
  ) {
    super();
    this.filterChange.subscribe(() => (this.paginator.pageIndex = 0));
  }

  get filter(): string {
    return this.filterChange.value;
  }

  set filter(filter: string) {
    this.filterChange.next(filter);
  }

  /** Stream con los cambios que afectan la renderizacion (CLIENT-SIDE) */
  connect(): Observable<UserDetails[]> {
    if (this.idSucursal) {
      this.exampleDatabase.getAllUsersByIdBranch(this.idSucursal);
    } else if (this.businessGroupId) {
      this.exampleDatabase.getAllUsers(this.businessGroupId);
    } else {
      this.exampleDatabase.dataChange.next([]);
    }

    const displayDataChanges = [
      this.exampleDatabase.dataChange,
      this._sort.sortChange,
      this.filterChange,
      this.paginator.page,
    ];

    return merge(...displayDataChanges).pipe(
      takeUntil(this.destroy$), // ✅ Desuscribirse cuando se destruye
      map(() => {
        // 1) Filtrado en cliente
        this.filteredData = this.exampleDatabase.data
          .slice()
          .filter((user: UserDetails) => {
            const searchStr = (
              (user.idUsuario ?? '') +
              (user.usr ?? '') +
              (user.idTipoUsuario ?? '') +
              (user.tipoUsuario ?? '') +
              (user.idGEMP ?? '') +
              (user.idSucursal ?? '') +
              (user.nombreSucursal ?? '') +
              (user.nombres ?? '') +
              (user.primerApellido ?? '') +
              (user.segundoApellido ?? '') +
              (user.idAsentamiento ?? '') +
              (user.nombreAsentamiento ?? '') +
              (user.idCP ?? '') +
              (user.codigoPostal ?? '') +
              (user.idMunicipio ?? '') +
              (user.municipio ?? '') +
              (user.domicilio ?? '') +
              (user.movil ?? '') +
              (user.email ?? '')
            ).toLowerCase();
            return searchStr.indexOf(this.filter.toLowerCase()) !== -1;
          });

        // 2) Ordenamiento en cliente
        const sortedData = this.sortData(this.filteredData.slice());

        // 3) Paginacion en cliente
        const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
        this.renderedData = sortedData.splice(startIndex, this.paginator.pageSize);

        return this.renderedData;
      })
    );
  }

  disconnect() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Ordenamiento CLIENT-SIDE */
  sortData(data: UserDetails[]): UserDetails[] {
    if (!this._sort.active || this._sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      let propertyA: number | string = '';
      let propertyB: number | string = '';

      switch (this._sort.active) {
        case 'nombre':
          [propertyA, propertyB] = [a.nombres, b.nombres];
          break;
        case 'tipo':
          [propertyA, propertyB] = [a.tipoUsuario, b.tipoUsuario];
          break;
        case 'telefono':
          [propertyA, propertyB] = [a.movil, b.movil];
          break;
        case 'direccion':
          [propertyA, propertyB] = [a.domicilio, b.domicilio];
          break;
        case 'municipio':
          [propertyA, propertyB] = [a.municipio, b.municipio];
          break;
        case 'cp':
          [propertyA, propertyB] = [a.codigoPostal, b.codigoPostal];
          break;
        case 'correo':
          [propertyA, propertyB] = [a.email, b.email];
          break;
        case 'asentamiento':
          [propertyA, propertyB] = [a.nombreAsentamiento, b.nombreAsentamiento];
          break;
        case 'sucursal':
          [propertyA, propertyB] = [a.nombreSucursal, b.nombreSucursal];
          break;
      }

      const valueA = isNaN(+propertyA) ? (propertyA ?? '') : +propertyA;
      const valueB = isNaN(+propertyB) ? (propertyB ?? '') : +propertyB;

      return ((valueA < valueB ? -1 : 1) * (this._sort.direction === 'asc' ? 1 : -1));
    });
  }
}
